import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const STATUS_COLORS = { confirmed:'badge-success', pending:'badge-warning', cancelled:'badge-error', completed:'badge-info', no_show:'badge-default' };

const formatTime = t => {
  if (!t) return '—';
  const [h,m] = t.split(':');
  const hour = parseInt(h);
  return `${hour>12?hour-12:hour||12}:${m} ${hour>=12?'PM':'AM'}`;
};

const formatDate = d => {
  if (!d) return '—';
  const parts = d.split('T')[0].split('-');
  const date = new Date(parseInt(parts[0]), parseInt(parts[1])-1, parseInt(parts[2]));
  return date.toLocaleDateString('en-US', { month:'short', day:'numeric', year:'numeric' });
};

const STATUS_ACTIONS = {
  pending:   [{ label:'Confirm',  status:'confirmed', style:'badge-success', message:'Are you sure you want to confirm this session?' },
              { label:'Cancel',   status:'cancelled', style:'badge-error',   message:'Are you sure you want to cancel this session? This cannot be undone.' }],
  confirmed: [{ label:'Complete', status:'completed', style:'badge-info',    message:'Mark this session as completed?' },
              { label:'No Show',  status:'no_show',   style:'badge-default', message:'Mark this session as a no show?' },
              { label:'Cancel',   status:'cancelled', style:'badge-error',   message:'Are you sure you want to cancel this session? This cannot be undone.' }],
  completed: [], cancelled: [], no_show: [],
};

function ConfirmModal({ action, session, onConfirm, onClose, updating }) {
  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-header">
          <span className="modal-title">CONFIRM ACTION</span>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <p style={{ color:'var(--text-secondary)', fontSize:'0.95rem', lineHeight:'1.6' }}>{action.message}</p>
          <div style={{ background:'var(--bg-elevated)', border:'1px solid var(--border)', borderRadius:'var(--radius-md)', padding:'12px 16px', marginTop:'4px' }}>
            <div style={{ fontSize:'0.8rem', color:'var(--text-muted)', marginBottom:'6px', fontWeight:'600', letterSpacing:'0.08em', textTransform:'uppercase' }}>Session Details</div>
            <div style={{ fontSize:'0.875rem', color:'var(--text-primary)', fontWeight:'500' }}>{session.instructor_first} {session.instructor_last}</div>
            <div style={{ fontSize:'0.875rem', color:'var(--text-secondary)' }}>{formatDate(session.session_date)} · {formatTime(session.start_time)} – {formatTime(session.end_time)}</div>
            <div style={{ fontSize:'0.875rem', color:'var(--text-secondary)' }}>{session.parent_first} {session.parent_last}</div>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline btn-sm" onClick={onClose} disabled={updating}>No, Go Back</button>
          <button
            className={`btn btn-sm ${action.status === 'cancelled' ? '' : 'btn-primary'}`}
            style={action.status === 'cancelled' ? { background:'var(--error)', color:'#fff' } : {}}
            onClick={onConfirm} disabled={updating}>
            {updating ? 'Updating...' : `Yes, ${action.label}`}
          </button>
        </div>
      </div>
    </div>
  );
}

function SessionDetailModal({ session, onClose, onUpdated }) {
  const [note, setNote] = useState(session.notes || '');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const saveNote = async () => {
    setSaving(true);
    try {
      await axios.patch(`/api/admin/sessions/${session.id}/note`, { note });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
      onUpdated();
    } catch (err) { console.error(err); }
    finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth:'560px' }}>
        <div className="modal-header">
          <span className="modal-title">SESSION DETAILS</span>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px', marginBottom:'4px' }}>
            {[
              ['Date', formatDate(session.session_date)],
              ['Time', `${formatTime(session.start_time)} – ${formatTime(session.end_time)}`],
              ['Instructor', `${session.instructor_first} ${session.instructor_last}`],
              ['Parent', `${session.parent_first} ${session.parent_last}`],
              ['Player', session.player_first ? `${session.player_first} ${session.player_last}` : '—'],
              ['Duration', `${session.duration_minutes} min`],
              ['Status', session.status],
              ['Email', session.parent_email || '—'],
            ].map(([label, value]) => (
              <div key={label} style={{ background:'var(--bg-elevated)', borderRadius:'var(--radius-md)', padding:'10px 14px' }}>
                <div style={{ fontSize:'0.7rem', color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.08em', fontWeight:'600', marginBottom:'4px' }}>{label}</div>
                <div style={{ fontSize:'0.875rem', color:'var(--text-primary)', fontWeight:'500', textTransform: label === 'Status' ? 'capitalize' : 'none' }}>{value}</div>
              </div>
            ))}
          </div>
          {session.cancellation_reason && (
            <div style={{ background:'rgba(248,113,113,0.08)', border:'1px solid rgba(248,113,113,0.2)', borderRadius:'var(--radius-md)', padding:'10px 14px' }}>
              <div style={{ fontSize:'0.7rem', color:'var(--error)', textTransform:'uppercase', letterSpacing:'0.08em', fontWeight:'600', marginBottom:'4px' }}>Cancellation Reason</div>
              <div style={{ fontSize:'0.875rem', color:'var(--text-secondary)' }}>{session.cancellation_reason}</div>
            </div>
          )}
          <div className="form-group">
            <label className="form-label">Admin Notes</label>
            <textarea className="form-input" rows={3} placeholder="Add internal notes about this session..." value={note} onChange={e => setNote(e.target.value)} style={{ resize:'vertical' }} />
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline btn-sm" onClick={onClose}>Close</button>
          <button className="btn btn-primary btn-sm" onClick={saveNote} disabled={saving}>
            {saving ? 'Saving...' : saved ? '✓ Saved' : 'Save Notes'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AdminSessions() {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rescheduleSession, setRescheduleSession] = useState(null);
  const [rescheduleForm, setRescheduleForm] = useState({ session_date: '', start_time: '', end_time: '', duration_minutes: 60 });
  const [rescheduleLoading, setRescheduleLoading] = useState(false);
  const [rescheduleError, setRescheduleError] = useState('');

  const openReschedule = (session) => {
    const dateStr = session.session_date ? session.session_date.split('T')[0] : '';
    setRescheduleForm({ session_date: dateStr, start_time: session.start_time || '', end_time: session.end_time || '', duration_minutes: session.duration_minutes || 60 });
    setRescheduleError('');
    setRescheduleSession(session);
  };

  const submitReschedule = async () => {
    if (!rescheduleForm.session_date || !rescheduleForm.start_time || !rescheduleForm.end_time) {
      setRescheduleError('Please fill in all fields.'); return;
    }
    if (rescheduleForm.start_time >= rescheduleForm.end_time) {
      setRescheduleError('End time must be after start time.'); return;
    }
    setRescheduleLoading(true); setRescheduleError('');
    try {
      await axios.patch(`/api/admin/sessions/${rescheduleSession.id}/reschedule`, rescheduleForm);
      setRescheduleSession(null);
      fetchSessions();
    } catch (err) {
      setRescheduleError(err.response?.data?.error || 'Failed to reschedule.');
    } finally { setRescheduleLoading(false); }
  };
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const [pending, setPending] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [detailSession, setDetailSession] = useState(null);
  const [selected, setSelected] = useState([]);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);

  const fetchSessions = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      const res = await axios.get('/api/admin/sessions', { params });
      setSessions(res.data.sessions);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [statusFilter]);

  useEffect(() => { fetchSessions(); }, [fetchSessions]);

  const filtered = sessions.filter(s => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      `${s.instructor_first} ${s.instructor_last}`.toLowerCase().includes(q) ||
      `${s.parent_first} ${s.parent_last}`.toLowerCase().includes(q) ||
      (s.player_first && `${s.player_first} ${s.player_last}`.toLowerCase().includes(q))
    );
  });

  useEffect(() => { setPage(1); }, [search, statusFilter]);
  const totalPages = Math.max(1, Math.ceil(filtered.length / perPage));
  const paginated = filtered.slice((page - 1) * perPage, page * perPage);

  const toggleSelect = id => setSelected(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);
  const toggleSelectAll = () => setSelected(selected.length === filtered.length ? [] : filtered.map(s => s.id));

  const bulkAction = async status => {
    if (!window.confirm(`Apply "${status}" to ${selected.length} session(s)?`)) return;
    try {
      await Promise.all(selected.map(id => axios.patch(`/api/admin/sessions/${id}/status`, { status })));
      setSelected([]);
      await fetchSessions();
    } catch (err) { console.error(err); }
  };

  const exportCSV = () => {
    const headers = ['Date','Time','Instructor','Parent','Player','Duration','Status','Notes'];
    const rows = filtered.map(s => [
      formatDate(s.session_date),
      `${formatTime(s.start_time)} - ${formatTime(s.end_time)}`,
      `${s.instructor_first} ${s.instructor_last}`,
      `${s.parent_first} ${s.parent_last}`,
      s.player_first ? `${s.player_first} ${s.player_last}` : '',
      `${s.duration_minutes} min`,
      s.status,
      (s.notes || '').replace(/,/g, ';'),
    ]);
    const csv = [headers, ...rows].map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type:'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'sessions.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  const confirmAction = async () => {
    setUpdating(true);
    try {
      await axios.patch(`/api/admin/sessions/${pending.session.id}/status`, { status: pending.action.status });
      setPending(null);
      await fetchSessions();
    } catch (err) { console.error(err); }
    finally { setUpdating(false); }
  };

  return (
    <div className="page">
      <div className="page-header"><h1 className="page-title">SESSIONS</h1><p className="page-subtitle">View and manage all training sessions</p></div>
      <div className="table-container">
        <div className="table-toolbar">
          <span className="table-toolbar-title">All Sessions</span>
          <input className="table-search" placeholder="Search by name..." value={search} onChange={e => setSearch(e.target.value)} />
          <select className="table-search" style={{ width:'160px' }} value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
            <option value="">All Statuses</option>
            <option value="pending">Pending</option>
            <option value="confirmed">Confirmed</option>
            <option value="cancelled">Cancelled</option>
            <option value="completed">Completed</option>
            <option value="no_show">No Show</option>
          </select>
          <select className="table-search" style={{ width:'130px' }} value={perPage} onChange={e => { setPerPage(Number(e.target.value)); setPage(1); }}>
            <option value={10}>10 / page</option>
            <option value={25}>25 / page</option>
            <option value={50}>50 / page</option>
          </select>
          <button className="btn btn-outline btn-sm" onClick={exportCSV} title="Export to CSV">↓ CSV</button>
        </div>

        {selected.length > 0 && (
          <div style={{ display:'flex', alignItems:'center', gap:'10px', padding:'10px 20px', background:'var(--bg-elevated)', borderBottom:'1px solid var(--border)' }}>
            <span style={{ fontSize:'0.85rem', color:'var(--text-secondary)' }}>{selected.length} selected</span>
            <button className="badge badge-success" style={{ cursor:'pointer', border:'none' }} onClick={() => bulkAction('confirmed')}>Confirm All</button>
            <button className="badge badge-info" style={{ cursor:'pointer', border:'none' }} onClick={() => bulkAction('completed')}>Complete All</button>
            <button className="badge badge-error" style={{ cursor:'pointer', border:'none' }} onClick={() => bulkAction('cancelled')}>Cancel All</button>
            <button className="btn btn-ghost btn-sm" onClick={() => setSelected([])}>Clear</button>
          </div>
        )}

        {loading ? (
          <div className="empty-state"><span className="empty-state-text">Loading...</span></div>
        ) : filtered.length === 0 ? (
          <div className="empty-state"><span className="empty-state-icon">📅</span><span className="empty-state-text">No sessions found</span></div>
        ) : (
          <table>
            <thead>
              <tr>
                <th style={{ width:'40px' }}><input type="checkbox" checked={selected.length === filtered.length && filtered.length > 0} onChange={toggleSelectAll} style={{ cursor:'pointer' }} /></th>
                <th>Date</th><th>Time</th><th>Instructor</th><th>Parent</th><th>Player</th><th>Status</th><th>Notes</th><th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginated.map(s => (
                <tr key={s.id}>
                  <td><input type="checkbox" checked={selected.includes(s.id)} onChange={() => toggleSelect(s.id)} style={{ cursor:'pointer' }} /></td>
                  <td className="td-primary" style={{ cursor:'pointer' }} onClick={() => setDetailSession(s)}>{formatDate(s.session_date)}</td>
                  <td>{formatTime(s.start_time)} – {formatTime(s.end_time)}</td>
                  <td>{s.instructor_first} {s.instructor_last}</td>
                  <td>{s.parent_first} {s.parent_last}</td>
                  <td>{s.player_first ? `${s.player_first} ${s.player_last}` : '—'}</td>
                  <td><span className={`badge ${STATUS_COLORS[s.status]||'badge-default'}`}>{s.status}</span></td>
                  <td>
                    {s.notes ? (
                      <span style={{ fontSize:'0.8rem', color:'var(--text-secondary)', cursor:'pointer', maxWidth:'120px', display:'inline-block', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }} onClick={() => setDetailSession(s)} title={s.notes}>
                        {s.notes}
                      </span>
                    ) : (
                      <button className="btn btn-ghost btn-sm" style={{ fontSize:'0.75rem' }} onClick={() => setDetailSession(s)}>+ Add</button>
                    )}
                  </td>
                  <td>
                    <div style={{ display:'flex', gap:'6px', flexWrap:'wrap' }}>
                      <button className="badge badge-default" style={{ cursor:'pointer', border:'none' }} onClick={() => setDetailSession(s)}>View</button>
                      {(STATUS_ACTIONS[s.status]||[]).map(action => (
                        <button key={action.status} className={`badge ${action.style}`}
                          style={{ cursor:'pointer', border:'none', padding:'4px 10px' }}
                          onClick={() => setPending({ session:s, action })}>
                          {action.label}
                        </button>
                      ))}
                      {['pending','confirmed'].includes(s.status) && (
                        <button className="badge badge-default"
                          style={{ cursor:'pointer', border:'none', padding:'4px 10px', background:'rgba(96,165,250,0.15)', color:'#60a5fa' }}
                          onClick={() => openReschedule(s)}>
                          Reschedule
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {!loading && filtered.length > 0 && (
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'12px 20px', borderTop:'1px solid var(--border)', flexWrap:'wrap', gap:'12px' }}>
            <span style={{ fontSize:'0.8rem', color:'var(--text-muted)' }}>
              Showing {Math.min((page-1)*perPage+1, filtered.length)}–{Math.min(page*perPage, filtered.length)} of {filtered.length}
            </span>
            {totalPages > 1 && (
              <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
                <button className="btn btn-outline btn-sm" onClick={() => setPage(p => Math.max(1, p-1))} disabled={page === 1}>← Prev</button>
                <span style={{ fontSize:'0.85rem', color:'var(--text-secondary)', minWidth:'90px', textAlign:'center' }}>Page {page} of {totalPages}</span>
                <button className="btn btn-outline btn-sm" onClick={() => setPage(p => Math.min(totalPages, p+1))} disabled={page === totalPages}>Next →</button>
              </div>
            )}
          </div>
        )}
      </div>

      {pending && <ConfirmModal action={pending.action} session={pending.session} onConfirm={confirmAction} onClose={() => setPending(null)} updating={updating} />}
      {detailSession && <SessionDetailModal session={detailSession} onClose={() => setDetailSession(null)} onUpdated={fetchSessions} />}
      {rescheduleSession && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setRescheduleSession(null)}>
          <div className="modal" style={{ maxWidth:'460px' }}>
            <div className="modal-header">
              <span className="modal-title">RESCHEDULE SESSION</span>
              <button className="modal-close" onClick={() => setRescheduleSession(null)}>✕</button>
            </div>
            <div className="modal-body">
              <p style={{ color:'var(--text-secondary)', fontSize:'0.875rem', marginBottom:'16px' }}>
                Rescheduling session. The parent will be notified automatically.
              </p>
              {rescheduleError && <div className="auth-error-banner" style={{ marginBottom:'12px' }}>⚠ {rescheduleError}</div>}
              <div className="form-group">
                <label className="form-label">New Date</label>
                <input type="date" className="form-input"
                  value={rescheduleForm.session_date}
                  min={new Date().toISOString().split('T')[0]}
                  onChange={e => setRescheduleForm(f => ({ ...f, session_date: e.target.value }))} />
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px' }}>
                <div className="form-group">
                  <label className="form-label">New Start Time</label>
                  <input type="time" className="form-input"
                    value={rescheduleForm.start_time}
                    onChange={e => setRescheduleForm(f => ({ ...f, start_time: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label className="form-label">New End Time</label>
                  <input type="time" className="form-input"
                    value={rescheduleForm.end_time}
                    onChange={e => setRescheduleForm(f => ({ ...f, end_time: e.target.value }))} />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Duration</label>
                <select className="form-input" value={rescheduleForm.duration_minutes}
                  onChange={e => setRescheduleForm(f => ({ ...f, duration_minutes: parseInt(e.target.value) }))}>
                  <option value={30}>30 minutes</option>
                  <option value={60}>60 minutes</option>
                </select>
              </div>
              <div style={{ background:'var(--bg-elevated)', borderRadius:'var(--radius-md)', padding:'12px 14px', marginTop:'8px' }}>
                <p style={{ fontSize:'0.75rem', color:'var(--text-muted)', margin:'0 0 6px', textTransform:'uppercase', letterSpacing:'0.08em', fontWeight:'600' }}>Current Schedule</p>
                <p style={{ fontSize:'0.875rem', color:'var(--text-secondary)', margin:'0' }}>
                  {rescheduleSession.session_date ? rescheduleSession.session_date.split('T')[0] : ''} at {rescheduleSession.start_time} — {rescheduleSession.end_time} ({rescheduleSession.duration_minutes} min)
                </p>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline btn-sm" onClick={() => setRescheduleSession(null)}>Cancel</button>
              <button className="btn btn-primary btn-sm" onClick={submitReschedule} disabled={rescheduleLoading}>
                {rescheduleLoading ? 'Saving...' : 'Save New Schedule'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
